import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useClientConfig, useUser } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { safeNext } from "./AuthLayout";

/**
 * Once someone is signed in (just now, or already when the screen opened), send them on:
 * to the verify-email gate when the backend requires it, else to `?next=`, else by `hasFamily`
 * (the app, or onboarding). `GET /api/user` bootstraps the user with `POST /api/user` on first sign-in.
 */
export function usePostAuthRedirect(defaultNoFamily = "/start") {
  const { ready, user } = useAuth();
  const { data: config } = useClientConfig();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get("next");
  const needsVerify = !!(config?.requireEmailVerification && user && !user.emailVerified);
  const userQuery = useUser();
  const appUser = needsVerify ? undefined : userQuery.data;

  useEffect(() => {
    if (!ready || !user) return;
    if (needsVerify) {
      const q = next ? `?next=${encodeURIComponent(safeNext(next, "/app"))}` : "";
      navigate(`/verify-email${q}`, { replace: true });
      return;
    }
    if (!appUser) return;
    const fallback = appUser.hasFamily ? "/app" : defaultNoFamily;
    navigate(safeNext(next, fallback), { replace: true });
  }, [ready, user, needsVerify, appUser, next, navigate, defaultNoFamily]);

  return { redirecting: !!(ready && user), userError: userQuery.error, retry: userQuery.refetch };
}
